import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { from, Observable } from 'rxjs';
import {
    catchError, concatMap, filter, map, switchMap, take, tap,
} from 'rxjs/operators';

import { transformToCartItems, transformToWishlist } from '../../application/services/wishlist-utils.service';
import { AddItemsWishlistUseCase$ } from '../../application/use-cases/add-wishlist.use-case';
import { GetIDsAsStringUseCase, MapWishlistItemsUseCase, SetModalErrorUseCase } from '../../application/use-cases/common-wishlist.use-case';
import { GetWishlistUseCase$ } from '../../application/use-cases/get-wishlist.use-case';
import { DeleteItemWishlistUseCase$ } from '../../application/use-cases/remove-wishlist.use-case';
import { Wishlist, WLItem } from '../../domain/models/wishlist.models';
import { WishlistApiRepository } from '../api/wishlist.api.repository';
import { WishlistSharedQueriesFacade } from '../facades/queries/shared.queries.facades';
import { WishlistQueriesFacade } from '../facades/queries/wishlist.queries.facades';

import {
    AddItemsWishlistAction,
    ClearWishlistAction,
    DeleteItemWishlistAction,
    GetWishlistAction,
    LoadWishlistAction,
} from './wishlist-commands.actions';
import {
    AddItemsWishlistSuccessAction,
    AddItemWishlistErrorAction,
    DeleteItemWishlistErrorAction,
    DeleteItemWishlistSuccessAction,
    GetWishlistErrorAction,
    GetWishlistSuccessAction,
} from './wishlist-events.actions';
import { BUYLATER_NAME, WISHLIST_NAME } from './wishlist.state';

import { StoreAndLanguage } from '@core/domain/state/core.state';
import { LoadModule } from '@core/infrastructure/state/core-commands.actions';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { ProductsArrayPublicCommands, ProductsArrayPublicEvents } from '@features/products/productsArray';
import { GoToSection } from '@shared/navigation';
import { LoadShopCartSuccessAndDeleteItemFromWishlist } from '@shared/shopcart/store/shopcart.actions';
import { LogoutAction } from '@shared/user/store';

const PENDING_WISHLIST_ITEM_COOKIE = 'pendingWishlistItem';

function getPayload(action: any): { itemToDelete: WLItem; onlyDelete?: boolean } {
    const payload = { itemToDelete: action.item };
    if ('onlyDelete' in action) {
        return { ...payload, onlyDelete: action.onlyDelete };
    }
    return payload;
}

@Injectable()
export class WishlistEffects {
    // eslint-disable-next-line max-len
    private readonly getStoreAndLanguage$: Observable<StoreAndLanguage> = this.wishlistSharedQueriesFacade.getStoreAndLanguage$();

    private readonly getCatalogId$: Observable<string> = this.wishlistSharedQueriesFacade.getCatalogId$();

    private readonly enableWish$: Observable<boolean> = this.wishlistSharedQueriesFacade.getCmsConfigValue$('enable_wishlist');

    private readonly whislistConfig$: Observable<string> = this.wishlistSharedQueriesFacade.getXConfigValue$('WISH_URL_SWITCH');

    constructor(
        private readonly actions$: Actions,
        private readonly wishlistSharedQueriesFacade: WishlistSharedQueriesFacade,
        private readonly wishlistQueriesFacade: WishlistQueriesFacade,
        private readonly wishlistApiRepository: WishlistApiRepository,
        private readonly coreStorageService: CoreStorageService,
    ) { }

    public resetWishWhenUserLogout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LogoutAction),
            map(() => ClearWishlistAction()),
        );
    });

    public loadWishlistIfNotLoaded$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadWishlistAction),
            concatLatestFrom(() => [
                this.wishlistSharedQueriesFacade.selectUserIsLogged$(),
                this.wishlistQueriesFacade.getWishlistStatus$(),
            ]),
            filter(([ _, userIsLogged, status ]) => !!userIsLogged && !status.loaded),
            map(() => GetWishlistAction()),
        );
    });

    /**
     * Efecto de carga del modulo de products array para poder hacer peticiones de producto en la wishlist
     */
    public loadProductsArrayModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadWishlistAction),
            map(() => {
                return LoadModule({
                    module: 'ProductsArrayFeatureCoreModule',
                    path: () => import('@features/products/productsArray/productsArray-feature.core.module').then((m) => m.ProductsArrayFeatureCoreModule),
                });
            })
        );
    });

    /**
     * Efecto que recupera la lista para el usuario actual
     */
    public getWishlist$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetWishlistAction),
            concatLatestFrom(() => [
                this.getStoreAndLanguage$,
                this.getCatalogId$,
                this.enableWish$,
                this.whislistConfig$,
                this.wishlistSharedQueriesFacade.selectUserIsLogged$(),
            ]),
            filter(([, , , , , userIsLogged]) => !!userIsLogged),
            switchMap(([ _, storeAndLanguage, catalogId, enableWish, whislistUrl ]) => {
                const wishName = enableWish ? WISHLIST_NAME : BUYLATER_NAME;
                return GetWishlistUseCase$(
                    this.wishlistApiRepository,
                    wishName,
                    storeAndLanguage,
                    catalogId,
                    whislistUrl
                ).pipe(
                    filter((apiWishlist) => !!apiWishlist?.items),
                    map((apiWishlist) => {
                        return ProductsArrayPublicCommands.LoadProductsArrayWithoutCategory({
                            productsId: GetIDsAsStringUseCase(apiWishlist?.items),
                            payload: { origin: 'getWishlist', wishlist: apiWishlist },
                        });
                    })
                );
            }),
        );
    });

    public getWishlistSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ProductsArrayPublicEvents.LoadProductsArrayWithoutCategorySuccess),
            filter(({ payload }) => payload?.origin === 'getWishlist' && payload?.wishlist),
            map(({ products, payload }) => MapWishlistItemsUseCase(payload.wishlist, products)),
            map((mapWish: Wishlist) => GetWishlistSuccessAction(mapWish)),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return from([GetWishlistErrorAction()]);
            })
        );
    });

    public goToLoginWhenNeeded$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddItemsWishlistAction),
            concatLatestFrom(() => this.wishlistSharedQueriesFacade.selectUserIsLogged$()),
            filter(([, userLogged]) => !userLogged),
            map(([payload]) => {
                this.coreStorageService.setValue(PENDING_WISHLIST_ITEM_COOKIE, payload, 'SESSION', 'TECHNICAL');
                return GoToSection({ url: 'userLoginApolo' });
            }),
        );
    });

    public addItemsAfterLogin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetWishlistAction),
            map(() => this.coreStorageService.getValue(PENDING_WISHLIST_ITEM_COOKIE, 'SESSION')),
            filter(payload => !!payload),
            tap(() => this.coreStorageService.removeValue(PENDING_WISHLIST_ITEM_COOKIE, 'SESSION')),
            concatLatestFrom(() => this.wishlistSharedQueriesFacade.selectUserIsLogged$()),
            filter(([, userLogged]) => !!userLogged),
            map(([payload]) => AddItemsWishlistAction(payload as any))
        );
    });

    public addItemsWishlist$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddItemsWishlistAction),
            concatLatestFrom(() => this.wishlistSharedQueriesFacade.selectUserIsLogged$()),
            filter(([, userLogged]) => !!userLogged),
            concatMap(([action]) => {
                return this.wishlistQueriesFacade.getWishlist$().pipe(
                    filter((wishlistStore) => !!wishlistStore?.name),
                    take(1),
                    concatLatestFrom(() => [
                        this.getStoreAndLanguage$,
                        this.getCatalogId$,
                        this.whislistConfig$,
                    ]),
                    switchMap(([wishlistStore, storeAndLanguage, catalogId, whislistUrl]) => {
                        return AddItemsWishlistUseCase$(
                            this.wishlistApiRepository,
                            WISHLIST_NAME,
                            wishlistStore,
                            transformToWishlist(action.items),
                            storeAndLanguage,
                            catalogId,
                            whislistUrl
                        ).pipe(
                            filter((apiWishlist) => !!apiWishlist?.items),
                            map((apiWishlist) => {
                                return ProductsArrayPublicCommands.LoadProductsArrayWithoutCategory({
                                    productsId: GetIDsAsStringUseCase(apiWishlist?.items),
                                    payload: {
                                        origin: 'addItemsWishlist',
                                        wishlist: apiWishlist,
                                        deleteItemCart: action.deleteItemCart,
                                        goToCheckout: action.goToCheckout,
                                        items: action.items,
                                    },
                                });
                            })
                        );
                    }),
                );
            })
        );
    });

    public addItemsWishlistSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ProductsArrayPublicEvents.LoadProductsArrayWithoutCategorySuccess),
            filter(({ payload }) => payload?.origin === 'addItemsWishlist' && payload?.wishlist),
            map(({ products, payload }) => {
                return AddItemsWishlistSuccessAction({
                    wishlist: MapWishlistItemsUseCase(payload.wishlist, products),
                    lastItems: transformToCartItems(payload.items),
                    deleteItemCart: payload.deleteItemCart,
                    goToCheckout: payload.goToCheckout,
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError((err: unknown) => {
                return from([AddItemWishlistErrorAction(), SetModalErrorUseCase(err)]);
            })
        );
    });

    public deleteItemWishlist$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteItemWishlistAction, LoadShopCartSuccessAndDeleteItemFromWishlist),
            map((action) => getPayload(action)),
            concatLatestFrom(() => [
                this.getStoreAndLanguage$,
                this.getCatalogId$,
                this.whislistConfig$,
                this.wishlistQueriesFacade.getWishlist$(),
            ]),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(([{ itemToDelete, onlyDelete }, storeAndLanguage, catalogId, whislistUrl, wishlistStore]) => {
                const wishlist = { ...wishlistStore };
                return DeleteItemWishlistUseCase$(
                    this.wishlistApiRepository,
                    WISHLIST_NAME,
                    wishlist,
                    itemToDelete,
                    storeAndLanguage,
                    catalogId,
                    whislistUrl
                ).pipe(
                    map((apiWishlist) => {
                        return {
                            apiWishlist,
                            deletedItem: itemToDelete,
                            onlyDelete,
                        };
                    }),
                );
            }),
            filter(({ apiWishlist }) => !!apiWishlist?.items),
            map(({ apiWishlist, deletedItem, onlyDelete }) => {
                return ProductsArrayPublicCommands.LoadProductsArrayWithoutCategory({
                    productsId: GetIDsAsStringUseCase(apiWishlist?.items),
                    payload: {
                        origin: onlyDelete ? 'onlyDeleteItemWishlist' : 'deleteItemWishlist',
                        wishlist: apiWishlist,
                        deletedItem,
                    },
                });
            })
        );
    });

    public deleteItemWishlistSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ProductsArrayPublicEvents.LoadProductsArrayWithoutCategorySuccess),
            filter(({ payload }) => payload?.origin === 'deleteItemWishlist' || payload?.origin === 'onlyDeleteItemWishlist' && payload?.wishlist),
            map(({ products, payload }) => {
                let mapWish: Wishlist;

                try {
                    mapWish = MapWishlistItemsUseCase(payload.wishlist, products);
                } catch (error: unknown) {
                    return DeleteItemWishlistErrorAction({ error });
                }

                return DeleteItemWishlistSuccessAction({
                    wishlist: mapWish,
                    deletedItem: payload.deletedItem,
                    origin: payload.origin,
                });
            }),
        );
    });

    public openDialogOnDeleteItemWishlistError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteItemWishlistErrorAction),
            map((err: unknown) => {
                return SetModalErrorUseCase(err);
            })
        );
    });
}
